/*
 * File: ProductMasterCard.tsx
 * Project: amazing-mumford
 * -----
 * Last Modified: 30 Dec 2020 10:07:02 pm
 * -----
 * Copyright (c) 2020 Elegant Software Solutions All rights reserved.
 */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useAuth0 } from '@auth0/auth0-react';
import { Card, Col, Divider, Row, Tooltip, Typography } from 'antd';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { POReceiptModel } from 'models/POReceiptModel';
import React, { FC } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { asyncFetchPOLineNumber, resetFilters, setSelectedReceipt } from 'redux/poReceipts/po-receipts-slice';

const styles: InlineStylesModel = {
  card: {
    borderRadius: 5
  },
  cardBody: {
    padding: 15,
    paddingRight: 20,
    paddingLeft: 20
  },
  largeText: {
    fontSize: '18px',
    fontFamily: 'Poppins',
    fontWeight: 700,
    color: '#000'
  },
  title: {
    fontSize: '15px',
    fontWeight: 500,
    color: '#636770'
  },
  subTitle: {
    fontSize: '15px',
    color: '#A0A3AE',
    minHeight: '27px'
  },
  text: {
    fontSize: '13px',
    color: '#A0A3AE'
  },
  smallText: {
    fontSize: '11px',
    color: 'lightGrey'
  },
  status: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#4E937A'
  },
  statusNA: {
    fontSize: '16px',
    fontWeight: 700,
    color: 'rgba(0,0,0,.4)'
  },
  draft: {
    color: 'orange'
  },
  posted: {
    color: '#4E937A'
  },
  soNumber: {
    fontSize: '14px',
    color: '#5868DD'
  },
  poNumber: {
    fontSize: '14px',
    color: 'gray'
  },
  customerId: {
    fontSize: '16px',
    color: '#636770'
  }
};

export const POReceiptCard: FC<POReceiptModel> = ({
  createdDateTime,
  deliveryMode,
  directDelivery,
  lines,
  orderedBy,
  purchaseOrderNumber,
  requestedBy,
  vendorAccount,
  vendorName,
  vendorOrderReference,
  warehouse
}: POReceiptModel): JSX.Element => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const createdDate = createdDateTime.slice(0, createdDateTime.indexOf('T'));

  const cardSelected = (): void => {
    dispatch(
      setSelectedReceipt({
        createdDateTime: createdDateTime,
        deliveryMode: deliveryMode,
        directDelivery: directDelivery,
        lines: lines,
        orderedBy: orderedBy,
        purchaseOrderNumber: purchaseOrderNumber,
        requestedBy: requestedBy,
        vendorAccount: vendorAccount,
        vendorName: vendorName,
        vendorOrderReference: vendorOrderReference,
        warehouse: warehouse
      })
    );
    dispatch(
      asyncFetchPOLineNumber({
        getAccessTokenSilently
      })
    );
    dispatch(resetFilters());
    history.push(`/${purchaseOrderNumber}`);
  };

  return (
    <Card onClick={cardSelected} hoverable style={styles.card} bodyStyle={styles.cardBody}>
      <Row gutter={8} justify="space-between">
        <Col span={12}>
          <Typography.Text style={styles.largeText}>
            <Tooltip title="Purchase Order Number">{purchaseOrderNumber}</Tooltip>
          </Typography.Text>
        </Col>
        <Col>
          <Typography.Text style={warehouse ? styles.status : styles.statusNA}>
            <Tooltip title="Warehouse">{warehouse || 'Not Available'}</Tooltip>
          </Typography.Text>
        </Col>
      </Row>
      <Row gutter={8} justify="space-between">
        <Col>
          <Typography style={styles.customerId}>
            <Tooltip title="Customer Order Number" placement="bottom">
              {vendorOrderReference}
            </Tooltip>
          </Typography>
        </Col>
        <Col>
          <Typography style={styles.soNumber}>
            <Tooltip title="Delivery Mode" placement="bottom">
              {deliveryMode || 'N/A'}
            </Tooltip>
          </Typography>
        </Col>
      </Row>
      <Divider />
      <Row justify="space-between">
        <Col style={{ textAlign: 'start', marginLeft: 10 }} span={10}>
          <Typography.Paragraph style={{ fontSize: '14px', color: 'gray' }}>
            <Tooltip title="Requested By" placement="top" autoAdjustOverflow={false} overlayInnerStyle={{ fontSize: 12, textAlign: 'center' }}>
              {requestedBy || 'N/A'}
            </Tooltip>
          </Typography.Paragraph>
          <Typography.Paragraph style={{ fontSize: '14px', color: 'gray' }}>
            <Tooltip title="Creation Date" placement="bottom" autoAdjustOverflow={false} overlayInnerStyle={{ fontSize: 12, textAlign: 'center' }}>
              {new Date(createdDateTime).toLocaleDateString() || 'Not available'}
            </Tooltip>
          </Typography.Paragraph>
        </Col>
        <Row>
          <Col span={2}>
            <Divider type="vertical" style={{ fontSize: 80 }} />
          </Col>
        </Row>

        <Col style={{ textAlign: 'end', marginRight: 10 }} span={10}>
          <Typography.Paragraph style={{ fontSize: '14px', color: 'gray' }}>
            <Tooltip title="Vendor Account" placement="top" autoAdjustOverflow={false} overlayInnerStyle={{ fontSize: 12, textAlign: 'center' }}>
              {vendorAccount}
            </Tooltip>
          </Typography.Paragraph>
          <Typography.Paragraph ellipsis style={{ fontSize: '14px', color: 'gray' }}>
            <Tooltip title="Vendor Name" placement="bottom" autoAdjustOverflow={false} overlayInnerStyle={{ fontSize: 12, textAlign: 'center' }}>
              {vendorName}
            </Tooltip>
          </Typography.Paragraph>
        </Col>
      </Row>
    </Card>
  );
};
